import { useEffect } from 'react';
import { useAuthTenantId, useAuthUserId } from '@/stores/auth.store';
import { useTenantId } from '@/stores/tenant.store';
import { useVoipStore } from '@/stores/voip.store';

/**
 * Registers the Telnyx WebRTC softphone while mounted (dashboard layout), so
 * Telnyx can ring this tab for inbound calls — registration must be live
 * *before* a call arrives, not lazily on first outbound dial.
 *
 * Keyed off the active tenant + user: org-switching changes the active tenant
 * in place (`tenant.store.setTenant`) without tearing down the dashboard, so on
 * a switch we unregister the previous tenant's SIP session and register a fresh
 * one — otherwise this tab would keep receiving the old org's calls. Also
 * unregisters on unmount (logout / leaving the dashboard).
 */
export function useVoipRegistration(): void {
  // Call every hook unconditionally (rules-of-hooks), then derive the active
  // tenant: the in-place org switch updates the tenant store, falling back to
  // the auth-store tenant on first load.
  const storeTenantId = useTenantId();
  const authTenantId = useAuthTenantId();
  const userId = useAuthUserId();
  const tenantId = storeTenantId ?? authTenantId;
  useEffect(() => {
    void useVoipStore.getState().register();
    return () => {
      useVoipStore.getState().unregister();
    };
  }, [tenantId, userId]);
}
